import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import "../styles/gallery.css";

import img1 from "../assets/Toni-Kroos-Bayern-debut-in-2007.jpg";
import img2 from "../assets/toni bayer.jpg";
import img3 from "../assets/toni bundes.jpg";
import img4 from "../assets/bayern ucl.webp";
import img5 from "../assets/kroos sign.jpg";
import img6 from "../assets/world cup win.jpg";
import img7 from "../assets/2016 ucl.jpg";
import img8 from "../assets/ucl 2017.webp";
import img9 from "../assets/ucl 2018.jpg";
import img10 from "../assets/2022 ucl.jpg";
import img11 from "../assets/2024 ucl.jpg";
import img12 from "../assets/freekick.jpg";
import img13 from "../assets/germany farewell.webp";
import img14 from "../assets/german debut.webp";
import img15 from "../assets/toniU17.jpg";
import img16 from "../assets/last ride.png";

/**
 * Raw data. Order here only matters as a tiebreaker for images from the
 * same year (the sort below is stable), so list same-year items in the
 * order they actually happened.
 */
const RAW_IMAGES = [
  {
    id: 15,
    src: img15,
    caption:
      "Player of the tournament and bronze shoe winner at the U-17 World Cup",
    year: 2007,
    category: "Germany",
  },
  {
    id: 1,
    src: img1,
    caption: "First professional appearance for Bayern Munich",
    year: 2007,
    category: "Bayern Munich",
  },
  {
    id: 2,
    src: img2,
    caption: "On loan at Bayer Leverkusen",
    year: 2009,
    category: "Bayer Leverkusen",
  },
  {
    id: 14,
    src: img14,
    caption: "First professional appearance for Germany",
    year: 2010,
    category: "Germany",
  },
  {
    id: 3,
    src: img3,
    caption: "Bundesliga champion with Bayern Munich",
    year: 2013,
    category: "Bayern Munich",
  },
  {
    id: 4,
    src: img4,
    caption: "Champions League winner with Bayern Munich",
    year: 2013,
    category: "Bayern Munich",
  },
  {
    id: 6,
    src: img6,
    caption: "World Cup winner with Germany",
    year: 2014,
    category: "Germany",
  },
  {
    id: 5,
    src: img5,
    caption: "Signing for Real Madrid",
    year: 2014,
    category: "Real Madrid",
  },
  {
    id: 7,
    src: img7,
    caption: "Champions League trophy with Real Madrid",
    year: 2016,
    category: "Real Madrid",
  },
  {
    id: 8,
    src: img8,
    caption: "Back-to-back Champions League wins with Real Madrid",
    year: 2017,
    category: "Real Madrid",
  },
  {
    id: 9,
    src: img9,
    caption: "Third consecutive Champions League with Real Madrid",
    year: 2018,
    category: "Real Madrid",
  },
  {
    id: 12,
    src: img12,
    caption: "Iconic last-minute free kick against Sweden",
    year: 2018,
    category: "Germany",
  },
  {
    id: 10,
    src: img10,
    caption: "Another Champions League win, his fifth",
    year: 2022,
    category: "Real Madrid",
  },
  {
    id: 16,
    src: img16,
    caption: "Farewell to Real Madrid after a decade",
    year: 2024,
    category: "Real Madrid",
  },
  {
    id: 11,
    src: img11,
    caption: "Sixth and final Champions League win, in his last club game",
    year: 2024,
    category: "Real Madrid",
  },
  {
    id: 13,
    src: img13,
    caption: "Last professional football match",
    year: 2024,
    category: "Germany",
  },
];

// Chronological: earliest -> latest (stable sort keeps same-year order above).
const GALLERY_IMAGES = [...RAW_IMAGES].sort((a, b) => a.year - b.year);

const FIRST_YEAR = GALLERY_IMAGES[0].year;
const LAST_YEAR = GALLERY_IMAGES[GALLERY_IMAGES.length - 1].year;

// Filter list, in order of first appearance in the timeline.
const CATEGORIES = [
  "All",
  ...new Set(GALLERY_IMAGES.map((image) => image.category)),
];

function GalleryComponent() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const closeBtnRef = useRef(null);
  const triggerRef = useRef(null);

  const filteredImages = useMemo(
    () =>
      activeFilter === "All"
        ? GALLERY_IMAGES
        : GALLERY_IMAGES.filter((image) => image.category === activeFilter),
    [activeFilter],
  );

  const isOpen = lightboxIndex !== null;
  const currentImage = isOpen ? filteredImages[lightboxIndex] : null;

  const openLightbox = (index, event) => {
    triggerRef.current = event.currentTarget;
    setLightboxIndex(index);
  };

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    triggerRef.current?.focus();
  }, []);

  const navigate = useCallback(
    (step) => {
      setLightboxIndex((index) => {
        const total = filteredImages.length;
        return (index + step + total) % total;
      });
    },
    [filteredImages.length],
  );

  // Keyboard controls, scroll lock, and initial focus while the lightbox is open.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowRight") navigate(1);
      if (event.key === "ArrowLeft") navigate(-1);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, closeLightbox, navigate]);

  return (
    <section className="gallery" aria-labelledby="gallery-title">
      <div className="gallery__inner">
        <header className="gallery__header">
          <h2 id="gallery-title" className="gallery__title">
            Career Highlights
          </h2>
          <p className="gallery__subtitle">
            {FIRST_YEAR} to {LAST_YEAR}
          </p>
        </header>

        <div
          className="gallery__filters"
          role="group"
          aria-label="Filter by team"
        >
          {CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              className="gallery__filter"
              aria-pressed={activeFilter === category}
              onClick={() => setActiveFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <ul className="gallery__grid">
          {filteredImages.map((image, index) => (
            <li key={image.id}>
              <button
                type="button"
                className="gallery__card"
                onClick={(event) => openLightbox(index, event)}
                aria-label={`View photo: ${image.caption}, ${image.year}`}
              >
                <div className="gallery__media">
                  <img
                    src={image.src}
                    alt={image.caption}
                    className="gallery__img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="gallery__body">
                  <span className="gallery__year">{image.year}</span>
                  <p className="gallery__caption">{image.caption}</p>
                  <span className="gallery__tag">{image.category}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {currentImage && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeLightbox();
          }}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="lightbox__btn lightbox__btn--close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ×
          </button>

          {filteredImages.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox__btn lightbox__btn--prev"
                onClick={() => navigate(-1)}
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                type="button"
                className="lightbox__btn lightbox__btn--next"
                onClick={() => navigate(1)}
                aria-label="Next photo"
              >
                ›
              </button>
            </>
          )}

          <figure className="lightbox__figure">
            <img
              src={currentImage.src}
              alt={currentImage.caption}
              className="lightbox__img"
            />
            <figcaption className="lightbox__meta">
              <span className="lightbox__year">{currentImage.year}</span>
              <p className="lightbox__caption">{currentImage.caption}</p>
              <div className="lightbox__counter">
                {lightboxIndex + 1} of {filteredImages.length}
              </div>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

export default GalleryComponent;
